/**
 * Virtual editable blocks
 *
 * Some editable text in a generated document doesn't live in the block content
 * tree: the document title (metadata.title) and the signature-page content
 * (heading, attestation clause, and each signatory's fields). These are
 * surfaced as "virtual" TextBlockMappings so they can be listed in the outline,
 * located/scrolled-to in the rendered PDF, and routed through the same
 * sidebar editor + save flow as ordinary content blocks.
 */

import { LegalDocument } from '@/schemas/schema';
import { DEFAULT_SIGNATURE_CONFIG } from './components/SignaturePage';
import { TextBlockMapping } from './text-block-mapper';

type Translate = (key: string, values?: Record<string, string | number>) => string;

export const VIRTUAL_TITLE_ID = 'metadata-title';
export const VIRTUAL_SIGNATURE_TITLE_ID = 'signature-title';
export const VIRTUAL_SIGNATURE_ATTESTATION_ID = 'signature-attestation';

/** Per-signatory field keys, in display order. */
const SIGNATORY_FIELDS = ['name', 'title', 'company', 'email', 'address'] as const;
type SignatoryField = (typeof SIGNATORY_FIELDS)[number];

/** True for any virtual (non-content-tree) block id. */
export function isVirtualBlockId(blockId: string | undefined): boolean {
  if (!blockId) return false;
  return (
    blockId === VIRTUAL_TITLE_ID ||
    blockId === 'metadata-effectiveDate' ||
    blockId.startsWith('signature-') ||
    blockId.startsWith('signatory-')
  );
}

/** Parse `signatory-{index}-{field}` → { index, field } (or null). */
function parseSignatoryId(blockId: string): { index: number; field: SignatoryField } | null {
  const match = /^signatory-(\d+)-(name|title|company|email|address)$/.exec(blockId);
  if (!match) return null;
  return { index: Number(match[1]), field: match[2] as SignatoryField };
}

function mapping(
  blockId: string,
  text: string,
  label: string,
  isTitle = false
): TextBlockMapping {
  return { blockId, blockPath: [], text, type: 'paragraph', isLeaf: true, isTitle, label };
}

export interface SignatoryVirtualGroup {
  /** Heading shown for this signatory's sub-group (their name, or "Signer N"). */
  label: string;
  items: TextBlockMapping[];
}

export interface VirtualBlocks {
  /** Document-level fields (currently just the title). */
  document: TextBlockMapping[];
  /** Signature-page general fields (heading, attestation). */
  signatureGeneral: TextBlockMapping[];
  /** One entry per signatory, each with its own editable fields. */
  signatories: SignatoryVirtualGroup[];
}

const FIELD_LABEL_KEYS: Record<SignatoryField, { key: string; fallback: string }> = {
  name: { key: 'GENERATEREVIEW_VBLOCKS_SIG_NAME', fallback: 'Name' },
  title: { key: 'GENERATEREVIEW_VBLOCKS_SIG_TITLE', fallback: 'Title / Role' },
  company: { key: 'GENERATEREVIEW_VBLOCKS_SIG_COMPANY', fallback: 'Company' },
  email: { key: 'GENERATEREVIEW_VBLOCKS_SIG_EMAIL', fallback: 'Email' },
  address: { key: 'GENERATEREVIEW_VBLOCKS_SIG_ADDRESS', fallback: 'Address' },
};

/**
 * Build the virtual editable blocks for a document. Empty signatory detail
 * fields are omitted (the name is always included), so the outline only lists
 * content that actually renders.
 */
export function buildVirtualBlocks(document: LegalDocument, t: Translate): VirtualBlocks {
  const title = document.metadata?.title ?? '';
  const documentBlocks: TextBlockMapping[] = title
    ? [
        mapping(
          VIRTUAL_TITLE_ID,
          title,
          t('GENERATEREVIEW_VBLOCKS_TITLE', { defaultValue: 'Document Title' }),
          true
        ),
      ]
    : [];

  const config = document.signaturePageConfig ?? {};
  const signatureGeneral: TextBlockMapping[] = [];
  const signatories: SignatoryVirtualGroup[] = [];

  if (document.signatories && document.signatories.length > 0) {
    signatureGeneral.push(
      mapping(
        VIRTUAL_SIGNATURE_TITLE_ID,
        config.title ?? DEFAULT_SIGNATURE_CONFIG.title,
        t('GENERATEREVIEW_VBLOCKS_SIG_HEADING', { defaultValue: 'Signature Heading' }),
        true
      ),
      mapping(
        VIRTUAL_SIGNATURE_ATTESTATION_ID,
        config.attestationClause ?? DEFAULT_SIGNATURE_CONFIG.attestationClause,
        t('GENERATEREVIEW_VBLOCKS_SIG_ATTESTATION', { defaultValue: 'Attestation Clause' })
      )
    );

    document.signatories.forEach((sig, index) => {
      const items: TextBlockMapping[] = [];
      SIGNATORY_FIELDS.forEach((field) => {
        const value = (sig[field] ?? '').trim();
        // Always offer the name; only list detail fields that have a value.
        if (field !== 'name' && !value) return;
        const { key, fallback } = FIELD_LABEL_KEYS[field];
        items.push(mapping(`signatory-${index}-${field}`, value, t(key, { defaultValue: fallback })));
      });
      const groupLabel =
        sig.name?.trim() ||
        t('GENERATEREVIEW_VBLOCKS_SIG_SIGNER', { defaultValue: `Signer ${index + 1}` });
      signatories.push({ label: groupLabel, items });
    });
  }

  return { document: documentBlocks, signatureGeneral, signatories };
}

/**
 * Apply an edit to a virtual block, returning a new document. Handles the
 * document title, the effective-date metadata line ("Label: value"), the
 * signature heading/attestation, and per-signatory fields.
 */
export function applyVirtualBlockEdit(
  document: LegalDocument,
  blockId: string,
  newText: string,
  effectiveDateLabelFallback: string
): LegalDocument {
  const updated = JSON.parse(JSON.stringify(document)) as LegalDocument;
  const value = newText.trim();

  if (blockId === VIRTUAL_TITLE_ID) {
    updated.metadata.title = value;
    return updated;
  }

  if (blockId === 'metadata-effectiveDate') {
    const colonIndex = value.indexOf(':');
    if (colonIndex > 0) {
      updated.metadata.effectiveDateLabel = value.substring(0, colonIndex + 1).trim();
      updated.metadata.effectiveDate = value.substring(colonIndex + 1).trim();
    } else {
      updated.metadata.effectiveDate = value;
      if (!updated.metadata.effectiveDateLabel) {
        updated.metadata.effectiveDateLabel = effectiveDateLabelFallback;
      }
    }
    return updated;
  }

  if (blockId === VIRTUAL_SIGNATURE_TITLE_ID || blockId === VIRTUAL_SIGNATURE_ATTESTATION_ID) {
    updated.signaturePageConfig = { ...(updated.signaturePageConfig ?? {}) };
    if (blockId === VIRTUAL_SIGNATURE_TITLE_ID) {
      updated.signaturePageConfig.title = value;
    } else {
      updated.signaturePageConfig.attestationClause = value;
    }
    return updated;
  }

  const signatory = parseSignatoryId(blockId);
  if (signatory && updated.signatories?.[signatory.index]) {
    updated.signatories[signatory.index][signatory.field] = value;
    return updated;
  }

  return updated;
}
